import { RequireKeys } from 'types';
import {
  FilterConfiguration,
  FilterConfigurations,
  FilterType,
} from '../TableWrapper.types';

type FilterOptions<T extends object> = Partial<FilterConfiguration<T>>;
type DateRangeFilterOptions<T extends object> = Extract<
  FilterConfiguration<T>,
  { type: FilterType.DATE_RANGE_PICKER }
>;
type RequiredDateRangeFilterOptions<T extends object> = RequireKeys<
  Partial<DateRangeFilterOptions<T>>,
  'dateFromKey' | 'dateToKey'
>;

export class TableFiltersBuilder<T extends object, U extends object = T> {
  private filters: FilterConfigurations<T, U> = {};
  private defaultOptions: FilterOptions<U>;
  constructor(defaultOptions: FilterOptions<U>) {
    this.defaultOptions = {
      ...defaultOptions,
    };
  }

  public addInputFilter(
    key: keyof T,
    filterOptions: FilterOptions<U> = {},
  ): this {
    const mergedOptions = this.mergeOptions(filterOptions);
    this.filters[key] = {
      ...mergedOptions,
      type: FilterType.INPUT,
    };
    return this;
  }
  public addNumberFilter(
    key: keyof T,
    filterOptions: FilterOptions<U> = {},
  ): this {
    const mergedOptions = this.mergeOptions(filterOptions);
    this.filters[key] = {
      ...mergedOptions,
      type: FilterType.NUMBER,
    };
    return this;
  }
  public addSelectFilter(
    key: keyof T,
    filterOptions: FilterOptions<U> = {},
  ): this {
    const mergedOptions = this.mergeOptions(filterOptions);
    this.filters[key] = {
      selectOptions: [],
      ...mergedOptions,
      type: FilterType.SELECT,
    };
    return this;
  }
  public addDateRangeFilter(
    key: keyof T,
    filterOptions: RequiredDateRangeFilterOptions<U>,
  ): this {
    const mergedOptions = this.mergeOptions(filterOptions);
    this.filters[key] = {
      dateFromKey: filterOptions.dateFromKey,
      dateToKey: filterOptions.dateToKey,
      ...mergedOptions,
      type: FilterType.DATE_RANGE_PICKER,
    } as FilterConfiguration<U>;
    return this;
  }
  public addCheckboxFilter(
    key: keyof T,
    filterOptions: FilterOptions<U> = {},
  ): this {
    const mergedOptions = this.mergeOptions(filterOptions);
    this.filters[key] = {
      ...mergedOptions,
      type: FilterType.CHECK_BOX,
    };
    return this;
  }

  private mergeOptions(options: FilterOptions<U>): FilterOptions<U> {
    return {
      ...this.defaultOptions,
      ...options,
    } as FilterOptions<U>;
  }

  public build(): FilterConfigurations<T, U> {
    return this.filters;
  }
}
